import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { MapPin, Search, Star } from '../icons/StatboxIcons'
import { fetchVenueDetails, formatVenueSearchError, searchVenues, stripInternalFields, stripPlusCodeFromAddress } from '../../lib/venuesApi'
import Loader from '../Loader'

/**
 * Google Places-style venue autocomplete.
 *
 * Controlled by SetupWizardLocations. The component owns its query state
 * and the dropdown UI; the parent handles persistence and the actual
 * venues array. Keyboard navigation (Arrow / Enter / Escape), outside-
 * click dismissal, race-safe async search, and a manual-fallback row
 * are all wired here so the page itself stays declarative.
 *
 * Props:
 *   - countryCode {string|null}  ISO-2 to bias mock results toward.
 *   - onSelect {(venue) => void} Called when the user picks a result.
 *   - onAddManual {(name) => void} Called for "Add '<query>' manually".
 *   - existingPlaceIds {Set<string>}  Used only to dim already-added rows;
 *       duplicate prevention itself lives in the parent so it can drive
 *       the pulse animation on the existing card.
 *   - autoFocus {boolean}
 *   - placeholder {string}
 *   - seedQuery {string}  When set, fills the input and opens the dropdown
 *       (used by parent suggestion chips). Cleared via onSeedApplied.
 *   - onSeedApplied {() => void}
 *   - embedded {boolean}  Softer input styling when nested inside a card.
 */
export default function VenueAutocomplete({
  countryCode = null,
  onSelect,
  onAddManual,
  existingPlaceIds,
  autoFocus = false,
  placeholder = 'Search for your venue...',
  seedQuery = '',
  onSeedApplied,
  embedded = false,
}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [highlightIdx, setHighlightIdx] = useState(-1)
  const [selecting, setSelecting] = useState(false)
  const [searchError, setSearchError] = useState('')

  const containerRef = useRef(null)
  const inputRef = useRef(null)
  const reqSeq = useRef(0)
  const reactId = useId()
  const listboxId = `venue-listbox-${reactId}`

  const trimmed = query.trim()
  const hasQuery = trimmed.length > 0

  useEffect(() => {
    const next = String(seedQuery || '').trim()
    if (!next) return
    setQuery(next)
    setOpen(true)
    inputRef.current?.focus()
    onSeedApplied?.()
  }, [seedQuery, onSeedApplied])

  // Race-safe search: every keystroke bumps reqSeq; only the most
  // recent in-flight request is allowed to commit results.
  useEffect(() => {
    if (!hasQuery) {
      setResults([])
      setLoading(false)
      return
    }
    const myReq = ++reqSeq.current
    setLoading(true)
    setSearchError('')
    let cancelled = false
    searchVenues(trimmed, countryCode)
      .then((rows) => {
        if (cancelled || myReq !== reqSeq.current) return
        setResults(rows)
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled || myReq !== reqSeq.current) return
        setResults([])
        setLoading(false)
        setSearchError(formatVenueSearchError(err))
      })
    return () => {
      cancelled = true
    }
  }, [trimmed, hasQuery, countryCode])

  // Reset highlight when results change so ArrowDown starts at row 0.
  useEffect(() => {
    setHighlightIdx(-1)
  }, [results, loading, hasQuery])

  // Outside click: collapse the dropdown without losing input focus
  // (spec wants Escape / outside / select to close it; focus blur is
  // also a natural close).
  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Build the flat list of selectable rows (= things ArrowDown can
  // land on). Loading / no-results headers are NOT in this list — they
  // render but aren't navigable.
  const selectable = useMemo(() => {
    if (!hasQuery || loading) return []
    if (results.length === 0) {
      return [
        { kind: 'manualInline', label: `Add "${trimmed}" manually` },
        { kind: 'manualFooter', label: "Can't find it? Add manually" },
      ]
    }
    return [
      ...results.map((v) => ({ kind: 'venue', venue: v })),
      { kind: 'manualFooter', label: "Can't find it? Add manually" },
    ]
  }, [results, loading, hasQuery, trimmed])

  const showDropdown = open && hasQuery

  const commitSelection = useCallback(
    async (idx) => {
      const row = selectable[idx]
      if (!row || selecting) return
      if (row.kind === 'venue') {
        setSelecting(true)
        try {
          const details = await fetchVenueDetails(row.venue.placeId)
          onSelect(stripInternalFields({
            ...row.venue,
            ...details,
            name: details.name || row.venue.name,
            formattedAddress:
              details.formattedAddress || row.venue.formattedAddress,
          }))
        } catch {
          onSelect(stripInternalFields(row.venue))
        } finally {
          setSelecting(false)
        }
      } else if (row.kind === 'manualInline' || row.kind === 'manualFooter') {
        if (!trimmed) return
        onAddManual(trimmed)
      }
      setQuery('')
      setOpen(false)
      setHighlightIdx(-1)
    },
    [selectable, onSelect, onAddManual, trimmed, selecting],
  )

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (showDropdown) {
        setOpen(false)
        e.preventDefault()
      }
      return
    }
    if (!showDropdown) {
      if (e.key === 'ArrowDown' && hasQuery) {
        setOpen(true)
        e.preventDefault()
      }
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (selectable.length === 0) return
      setHighlightIdx((i) => (i + 1) % selectable.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (selectable.length === 0) return
      setHighlightIdx((i) =>
        i <= 0 ? selectable.length - 1 : i - 1,
      )
    } else if (e.key === 'Enter') {
      if (selectable.length === 0) return
      e.preventDefault()
      // Spec: Enter selects highlighted row, or first row if none
      // highlighted. The footer is a "select first" no-op for results,
      // so the manual-footer case only hits when results are empty.
      const idx = highlightIdx >= 0 ? highlightIdx : 0
      commitSelection(idx)
    }
  }

  const optionId = (idx) => `${listboxId}-opt-${idx}`
  const activeId =
    showDropdown && highlightIdx >= 0 ? optionId(highlightIdx) : undefined

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[var(--signup-text-tertiary)]"
          size={18}
          strokeWidth={2}
          aria-hidden
        />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-activedescendant={activeId}
          aria-label="Search for your venue"
          autoComplete="off"
          spellCheck={false}
          autoFocus={autoFocus}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={() => {
            if (hasQuery) setOpen(true)
          }}
          onKeyDown={handleKeyDown}
          disabled={selecting}
          placeholder={placeholder}
          className={`w-full h-[52px] pl-11 pr-4 rounded-lg border text-[15px] text-[var(--signup-text-primary)] placeholder:text-[var(--signup-text-tertiary)] focus:outline-none focus:border-[var(--signup-accent)] focus:ring-[3px] focus:ring-[var(--signup-accent-ring)] motion-safe:transition-[border-color,box-shadow] motion-safe:duration-150 disabled:opacity-60 ${
            embedded
              ? 'bg-[var(--signup-bg-subtle)] border-[var(--signup-border-subtle)]'
              : 'bg-[var(--signup-bg-elevated)] border-[var(--signup-border-default)]'
          }`}
        />
      </div>

      {searchError ? (
        <p role="alert" className="mt-2 text-[13px] text-[#dc2626] leading-snug">
          {searchError}
          {' '}
          <span className="text-slate-600">You can enter the venue manually below.</span>
        </p>
      ) : null}

      {showDropdown && (
        <div
          id={listboxId}
          role="listbox"
          aria-label="Venue suggestions"
          className="absolute left-0 right-0 z-30 mt-1 max-h-[320px] overflow-y-auto rounded-lg bg-[var(--signup-bg-elevated)] border border-[var(--signup-border-subtle)]"
          style={{ boxShadow: 'var(--signup-shadow-lg)' }}
        >
          {loading || selecting ? (
            <div className="flex items-center gap-3 px-3 py-3 text-[13px] text-[var(--signup-text-tertiary)]">
              <Loader size={14} />
              {selecting ? 'Adding venue…' : 'Searching…'}
            </div>
          ) : results.length > 0 ? (
            <ResultRows
              results={results}
              highlightIdx={highlightIdx}
              setHighlightIdx={setHighlightIdx}
              commitSelection={commitSelection}
              optionId={optionId}
              existingPlaceIds={existingPlaceIds}
            />
          ) : (
            <NoResultsRow
              query={trimmed}
              isHighlighted={highlightIdx === 0}
              onHover={() => setHighlightIdx(0)}
              onClick={() => commitSelection(0)}
              optionId={optionId(0)}
            />
          )}

          {/* Footer "Can't find it? Add manually" — always present
              when the dropdown has any visible content. */}
          {!loading && (
            <FooterRow
              isHighlighted={highlightIdx === selectable.length - 1}
              onHover={() => setHighlightIdx(selectable.length - 1)}
              onClick={() => commitSelection(selectable.length - 1)}
              optionId={optionId(selectable.length - 1)}
              hasResults={results.length > 0}
            />
          )}
        </div>
      )}
    </div>
  )
}

/* ---------- Subcomponents ---------- */

function VenueResultPhoto({ photoUrl, name }) {
  const [errored, setErrored] = useState(false)
  if (!photoUrl || errored) {
    return (
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[var(--signup-accent-subtle)]"
        aria-hidden
      >
        <MapPin size={16} className="text-[var(--signup-accent)]" strokeWidth={2.25} />
      </div>
    )
  }
  return (
    <img
      src={photoUrl}
      alt=""
      loading="lazy"
      className="h-10 w-10 shrink-0 rounded-md object-cover bg-[var(--signup-bg-subtle)]"
      onError={() => setErrored(true)}
    />
  )
}

function ResultRows({
  results,
  highlightIdx,
  setHighlightIdx,
  commitSelection,
  optionId,
  existingPlaceIds,
}) {
  return (
    <ul className="py-1">
      {results.map((venue, idx) => {
        const isHighlighted = highlightIdx === idx
        const alreadyAdded = existingPlaceIds?.has(venue.placeId)
        return (
          <li
            key={venue.placeId}
            id={optionId(idx)}
            role="option"
            aria-selected={isHighlighted}
            onMouseEnter={() => setHighlightIdx(idx)}
            // Use mousedown so the row commits before the input loses
            // focus and the outside-click handler races us.
            onMouseDown={(e) => {
              e.preventDefault()
              commitSelection(idx)
            }}
            className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer ${
              isHighlighted ? 'bg-[var(--signup-bg-subtle)]' : 'bg-transparent'
            } ${alreadyAdded ? 'opacity-55' : ''}`}
          >
            <VenueResultPhoto photoUrl={venue.photoUrl} name={venue.name} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[14px] font-medium text-[var(--signup-text-primary)]">
                {venue.name}
                {alreadyAdded && (
                  <span className="ml-2 align-middle text-[11px] font-normal text-[var(--signup-text-tertiary)]">
                    · added
                  </span>
                )}
              </div>
              <div className="truncate text-[13px] text-[var(--signup-text-secondary)] flex items-center gap-1.5">
                <MapPin
                  size={12}
                  className="shrink-0 text-[var(--signup-accent)]"
                  strokeWidth={2.25}
                  aria-hidden
                />
                <span className="truncate">
                  {stripPlusCodeFromAddress(venue.formattedAddress)}
                </span>
              </div>
            </div>
            {typeof venue.rating === 'number' && (
              <div className="shrink-0 inline-flex items-center gap-1 text-[12px] text-[var(--signup-text-tertiary)]">
                <Star size={12} fill="#f59e0b" stroke="#f59e0b" aria-hidden />
                <span className="text-[var(--signup-text-secondary)]">
                  {venue.rating.toFixed(1)}
                </span>
                {typeof venue.ratingCount === 'number' && (
                  <span>({venue.ratingCount})</span>
                )}
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}

function NoResultsRow({ query, isHighlighted, onHover, onClick, optionId }) {
  return (
    <div className="px-3 py-3">
      <div className="text-[13px] text-[var(--signup-text-secondary)]">
        No venues found for{' '}
        <span className="text-[var(--signup-text-primary)]">"{query}"</span>.
      </div>
      <button
        type="button"
        id={optionId}
        role="option"
        aria-selected={isHighlighted}
        onMouseEnter={onHover}
        onMouseDown={(e) => {
          e.preventDefault()
          onClick()
        }}
        className={`mt-1.5 inline-flex items-center gap-1 rounded text-left text-[12.5px] focus:outline-none ${
          isHighlighted
            ? 'text-[var(--signup-accent-hover)]'
            : 'text-[var(--signup-accent)] hover:text-[var(--signup-accent-hover)]'
        }`}
      >
        <MapPin size={12} aria-hidden />
        <span>Add "{query}" manually →</span>
      </button>
    </div>
  )
}

function FooterRow({
  isHighlighted,
  onHover,
  onClick,
  optionId,
  hasResults,
}) {
  return (
    <>
      {hasResults && (
        <div className="mx-3 my-1 h-px bg-[var(--signup-border-subtle)]" />
      )}
      <button
        type="button"
        id={optionId}
        role="option"
        aria-selected={isHighlighted}
        onMouseEnter={onHover}
        onMouseDown={(e) => {
          e.preventDefault()
          onClick()
        }}
        className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13px] motion-safe:transition-colors motion-safe:duration-100 ${
          isHighlighted
            ? 'bg-[var(--signup-bg-subtle)] text-[var(--signup-text-primary)]'
            : 'bg-transparent text-[var(--signup-text-secondary)] hover:bg-[var(--signup-bg-subtle)] hover:text-[var(--signup-text-primary)]'
        }`}
      >
        <MapPin
          size={14}
          className="text-[var(--signup-accent)]"
          strokeWidth={2.25}
          aria-hidden
        />
        <span>Can't find it? </span>
        <span className="font-medium text-[var(--signup-accent)]">
          Add manually
        </span>
      </button>
    </>
  )
}
